/**
 * Server functions for CIH challenges.
 *
 * Challenges are L1 pillar content pages managed by admins.
 * Lifecycle transitions delegate to content-lifecycle helpers.
 *
 * Aliases: challenges, cih challenges, challenge crud, challenge lifecycle
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import { ContentStatus } from "~/lib/schemas/db-enums-cih";
import type {
  ChallengeListRow,
  PaginatedChallengeCoaches,
} from "~/lib/server/cih/challenges";
import type { JsonValue } from "~/lib/shared/json";
import { zJsonBody, zTrimmedNullable } from "~/lib/shared/zod-helpers";

export type { ChallengeListRow } from "~/lib/server/cih/challenges";

// ============================================================================
// Types
// ============================================================================

export interface ChallengeRow {
  id: string;
  slug: string | null;
  title: string;
  subtitle: string | null;
  description: string;
  body: JsonValue | null;
  heroImageUrl: string | null;
  tagline: string | null;
  icon: string | null;
  faqs: { question: string; answer: string }[] | null;
  reviewerName: string | null;
  reviewerCredentials: string | null;
  reviewedDate: string | null;
  intakeTrigger: string | null;
  status: string;
  authorId: string | null;
  publishedAt: Date | null;
  version: number;
  draftOf: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

// ============================================================================
// Query Keys
// ============================================================================

export const challengeQueryKeys = {
  all: ["challenges"] as const,
  list: (status?: string) => ["challenges", "list", status ?? "all"] as const,
  detail: (id: string) => ["challenges", id] as const,
  published: (slug: string) => ["challenges", "published", slug] as const,
  publishedList: ["challenges", "published", "list"] as const,
};

// ============================================================================
// Server Functions
// ============================================================================

/**
 * List all challenges (admin). Excludes draft-of rows from the main list, but
 * surfaces each row's `pendingDraftId` so the admin UI can link to MCP-created
 * (or admin-created) drafts of published challenges.
 * Optionally filter by status.
 */
export const getChallengesFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ status: z.string().optional() }).optional())
  .handler(async ({ data }): Promise<ChallengeListRow[]> => {
    const { listChallenges } = await import("~/lib/server/cih/challenges");
    return listChallenges(data);
  });

/**
 * Get a single challenge by ID (admin).
 */
export const getChallengeFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<ChallengeRow | null> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challenges } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");
    const { resolveStoredImageUrl } = await import("~/lib/server/s3");

    const db = await getDBWithInit();
    const [row] = await db
      .select()
      .from(challenges)
      .where(eq(challenges.id, data.id));
    if (!row) return null;
    return {
      ...row,
      heroImageUrl: resolveStoredImageUrl(row.heroImageUrl),
    };
  });

/**
 * Get a published challenge by slug (public — no auth required).
 */
export const getPublishedChallengeFn = createServerFn({ method: "POST" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<ChallengeRow | null> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challenges } = await import("~/../drizzle/schema");
    const { and, eq } = await import("drizzle-orm");
    const { resolveStoredImageUrl } = await import("~/lib/server/s3");

    const db = await getDBWithInit();
    const [row] = await db
      .select()
      .from(challenges)
      .where(
        and(
          eq(challenges.slug, data.slug),
          eq(challenges.status, ContentStatus.Published),
        ),
      );
    if (!row) return null;
    return { ...row, heroImageUrl: resolveStoredImageUrl(row.heroImageUrl) };
  });

/**
 * Create a new challenge (admin). authorId is set from the session.
 */
export const createChallengeFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      title: z.string().min(1),
      description: z.string().min(1),
      slug: z.string().optional(),
      subtitle: z.string().optional(),
      body: z.unknown().optional(),
      heroImageUrl: z.string().url().optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<ChallengeRow> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challenges } = await import("~/../drizzle/schema");

    const db = await getDBWithInit();
    const [row] = await db
      .insert(challenges)
      .values({
        title: data.title,
        description: data.description,
        slug: data.slug ?? null,
        subtitle: data.subtitle ?? null,
        body: (data.body as JsonValue) ?? null,
        heroImageUrl: data.heroImageUrl ?? null,
        authorId: context.user.id,
      })
      .returning();
    return row;
  });

/**
 * Update challenge fields (admin, partial update).
 */
export const updateChallengeFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      id: z.string().uuid(),
      title: z.string().trim().min(1).optional(),
      description: z.string().trim().min(1).optional(),
      slug: zTrimmedNullable(),
      subtitle: zTrimmedNullable(),
      body: zJsonBody(),
      heroImageUrl: zTrimmedNullable(),
      tagline: zTrimmedNullable(),
      icon: zTrimmedNullable(),
      faqs: z
        .array(z.object({ question: z.string(), answer: z.string() }))
        .optional(),
      reviewerName: zTrimmedNullable(),
      reviewerCredentials: zTrimmedNullable(),
      reviewedDate: zTrimmedNullable(),
      intakeTrigger: zTrimmedNullable(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challenges } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const { id, ...fields } = data;
    const updates: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) updates[key] = value;
    }
    if (Object.keys(updates).length === 0) return { success: true };

    await db.update(challenges).set(updates).where(eq(challenges.id, id));
    return { success: true };
  });

/**
 * Admin self-publish a draft challenge (skips review).
 */
export const publishChallengeFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { challenges } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.publish(challenges, data.id, context.user.id);
    return { success: true };
  });

/**
 * Archive a published challenge.
 */
export const archiveChallengeFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { challenges } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.archive(challenges, data.id, context.user.id);
    return { success: true };
  });

/**
 * Create an editable draft of a published challenge.
 */
export const createChallengeDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<ChallengeRow> => {
    const { challenges, challengeFrameworks, challengesExpertiseCategories } =
      await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    return lifecycle.createDraftOfPublished(
      challenges,
      data.publishedId,
      context.user.id,
      [
        {
          table: challengeFrameworks,
          fkColumn: challengeFrameworks.challengeId,
        },
        {
          table: challengesExpertiseCategories,
          fkColumn: challengesExpertiseCategories.challengeId,
        },
      ],
    ) as unknown as Promise<ChallengeRow>;
  });

/**
 * Promote a draft back onto its published parent.
 */
export const promoteChallengeDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ draftId: z.string().uuid() }))
  .handler(async ({ data }): Promise<ChallengeRow> => {
    const { challenges, challengeFrameworks, challengesExpertiseCategories } =
      await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    return lifecycle.promoteDraft(challenges, data.draftId, [
      {
        table: challengeFrameworks,
        fkColumn: challengeFrameworks.challengeId,
      },
      {
        table: challengesExpertiseCategories,
        fkColumn: challengesExpertiseCategories.challengeId,
      },
    ]) as unknown as Promise<ChallengeRow>;
  });

/**
 * Restore an archived challenge to draft.
 */
export const restoreChallengeFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { challenges } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.restoreFromArchive(challenges, data.id, context.user.id);
    return { success: true };
  });

/**
 * Approve an in_review challenge (publish it).
 */
export const approveChallengeFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { challenges } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.approve(challenges, data.id, context.user.id);
    return { success: true };
  });

/**
 * Return an in_review challenge to draft with feedback.
 */
export const returnChallengeToDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid(), note: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { challenges } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.returnToDraft(
      challenges,
      data.id,
      context.user.id,
      data.note,
    );
    return { success: true };
  });

/**
 * Submit a challenge draft for review.
 */
export const submitChallengeForReviewFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { challenges } = await import("~/../drizzle/schema");
    const lifecycle = await import("~/lib/server/cih/content-lifecycle");
    await lifecycle.submitForReview(challenges, data.id, context.user.id);
    return { success: true };
  });

// ============================================================================
// Coach roster
// ============================================================================

/**
 * Get coaches whose expertise categories overlap with a challenge (public).
 * Path: challenge → challengesExpertiseCategories → expertiseSubcategories → coachExpertise → coaches.
 *
 * Supports pagination via `page` and `limit`. When omitted, returns the first
 * page at the default limit. `total` reflects the full filtered coach count.
 */
export const getCoachesByChallengeFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      challengeId: z.string().uuid(),
      page: z.number().int().min(1).optional(),
      limit: z.number().int().min(1).max(100).optional(),
    }),
  )
  .handler(async ({ data }): Promise<PaginatedChallengeCoaches> => {
    const { getCoachesByChallenge } =
      await import("~/lib/server/cih/challenges");
    return getCoachesByChallenge(data);
  });

/**
 * Get expertise category IDs linked to a challenge (admin).
 */
export const getChallengeExpertiseCategoryIdsFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ challengeId: z.string().uuid() }))
  .handler(async ({ data }): Promise<string[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challengesExpertiseCategories } =
      await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const rows = await db
      .select({
        categoryId: challengesExpertiseCategories.expertiseCategoryId,
      })
      .from(challengesExpertiseCategories)
      .where(eq(challengesExpertiseCategories.challengeId, data.challengeId));
    return rows.map((r) => r.categoryId);
  });

/**
 * Sync expertise categories for a challenge (admin).
 * Computes diff: deletes removed, inserts new.
 */
export const syncChallengeExpertiseCategoriesFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      challengeId: z.string().uuid(),
      categoryIds: z.array(z.string().uuid()),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challengesExpertiseCategories } =
      await import("~/../drizzle/schema");
    const { eq, and, inArray } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const existing = await db
      .select({
        categoryId: challengesExpertiseCategories.expertiseCategoryId,
      })
      .from(challengesExpertiseCategories)
      .where(eq(challengesExpertiseCategories.challengeId, data.challengeId));

    const existingIds = new Set(existing.map((r) => r.categoryId));
    const desiredIds = new Set(data.categoryIds);

    const toAdd = data.categoryIds.filter((id) => !existingIds.has(id));
    const toRemove = [...existingIds].filter((id) => !desiredIds.has(id));

    if (toRemove.length > 0) {
      await db
        .delete(challengesExpertiseCategories)
        .where(
          and(
            eq(challengesExpertiseCategories.challengeId, data.challengeId),
            inArray(
              challengesExpertiseCategories.expertiseCategoryId,
              toRemove,
            ),
          ),
        );
    }

    if (toAdd.length > 0) {
      await db.insert(challengesExpertiseCategories).values(
        toAdd.map((categoryId) => ({
          challengeId: data.challengeId,
          expertiseCategoryId: categoryId,
        })),
      );
    }

    return { success: true };
  });

/**
 * Get framework IDs linked to a challenge (admin).
 */
export const getChallengeFrameworkIdsFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ challengeId: z.string().uuid() }))
  .handler(async ({ data }): Promise<string[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challengeFrameworks } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const rows = await db
      .select({ frameworkId: challengeFrameworks.frameworkId })
      .from(challengeFrameworks)
      .where(eq(challengeFrameworks.challengeId, data.challengeId));
    return rows.map((r) => r.frameworkId);
  });

/**
 * Sync framework links for a challenge (admin).
 * Computes diff: deletes removed, inserts new.
 */
export const syncChallengeFrameworksFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      challengeId: z.string().uuid(),
      frameworkIds: z.array(z.string().uuid()),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challengeFrameworks } = await import("~/../drizzle/schema");
    const { eq, and, inArray } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const existing = await db
      .select({ frameworkId: challengeFrameworks.frameworkId })
      .from(challengeFrameworks)
      .where(eq(challengeFrameworks.challengeId, data.challengeId));

    const existingIds = new Set(existing.map((r) => r.frameworkId));
    const desiredIds = new Set(data.frameworkIds);

    const toAdd = data.frameworkIds.filter((id) => !existingIds.has(id));
    const toRemove = [...existingIds].filter((id) => !desiredIds.has(id));

    await db.transaction(async (tx) => {
      if (toRemove.length > 0) {
        await tx
          .delete(challengeFrameworks)
          .where(
            and(
              eq(challengeFrameworks.challengeId, data.challengeId),
              inArray(challengeFrameworks.frameworkId, toRemove),
            ),
          );
      }

      if (toAdd.length > 0) {
        await tx.insert(challengeFrameworks).values(
          toAdd.map((frameworkId) => ({
            challengeId: data.challengeId,
            frameworkId,
          })),
        );
      }
    });

    return { success: true };
  });

// ============================================================================
// Published challenge with relations (for detail page trust signals)
// ============================================================================

export interface ChallengeWithRelations extends ChallengeRow {
  frameworks: { id: string; name: string; slug: string | null; type: string }[];
  relatedChallenges: { id: string; title: string; slug: string | null }[];
}

/**
 * Get a published challenge by slug with linked frameworks and related challenges.
 * Used by the challenge detail page for trust signals.
 */
export const getPublishedChallengeWithRelationsFn = createServerFn({
  method: "POST",
})
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<ChallengeWithRelations | null> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challenges, challengeFrameworks, frameworks, challengeRelated } =
      await import("~/../drizzle/schema");
    const { and, eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const [challenge] = await db
      .select()
      .from(challenges)
      .where(
        and(
          eq(challenges.slug, data.slug),
          eq(challenges.status, ContentStatus.Published),
        ),
      );

    if (!challenge) return null;

    const linkedFrameworks = await db
      .select({
        id: frameworks.id,
        name: frameworks.name,
        slug: frameworks.slug,
        type: frameworks.type,
      })
      .from(challengeFrameworks)
      .innerJoin(frameworks, eq(challengeFrameworks.frameworkId, frameworks.id))
      .where(
        and(
          eq(challengeFrameworks.challengeId, challenge.id),
          eq(frameworks.status, ContentStatus.Published),
        ),
      );

    const related = await db
      .select({
        id: challenges.id,
        title: challenges.title,
        slug: challenges.slug,
      })
      .from(challengeRelated)
      .innerJoin(
        challenges,
        eq(challengeRelated.relatedChallengeId, challenges.id),
      )
      .where(
        and(
          eq(challengeRelated.challengeId, challenge.id),
          eq(challenges.status, ContentStatus.Published),
        ),
      );

    return {
      ...challenge,
      frameworks: linkedFrameworks,
      relatedChallenges: related,
    };
  });

/**
 * Get related challenge IDs for a given challenge (admin).
 */
export const getChallengeRelatedIdsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ challengeId: z.string().uuid() }))
  .handler(async ({ data }): Promise<string[]> => {
    const { getChallengeRelatedIds } =
      await import("~/lib/server/cih/challenges");
    return getChallengeRelatedIds({ challengeId: data.challengeId });
  });

/**
 * Sync related challenge links (diff/insert/delete), symmetric rows.
 */
export const syncChallengeRelatedFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      challengeId: z.string().uuid(),
      relatedChallengeIds: z.array(z.string().uuid()),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { syncChallengeRelated } =
      await import("~/lib/server/cih/challenges");
    return syncChallengeRelated({
      challengeId: data.challengeId,
      relatedChallengeIds: data.relatedChallengeIds,
    });
  });

// ============================================================================
// Query Options
// ============================================================================

export const challengesQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: challengeQueryKeys.list(status),
    queryFn: () => getChallengesFn({ data: { status } }),
  });

export const challengeQueryOptions = (id: string) =>
  queryOptions({
    queryKey: challengeQueryKeys.detail(id),
    queryFn: () => getChallengeFn({ data: { id } }),
  });

export const publishedChallengeQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: challengeQueryKeys.published(slug),
    queryFn: () => getPublishedChallengeFn({ data: { slug } }),
  });

export interface PublishedChallengeListRow {
  id: string;
  title: string;
  slug: string | null;
  subtitle: string | null;
  description: string;
  tagline: string | null;
  icon: string | null;
  frameworks: number;
  articles: number;
}

/**
 * List published challenges for the public browse page (no auth required).
 * Includes aggregate counts of linked frameworks and published articles so
 * cards can show browsing density.
 */
export const getPublishedChallengesFn = createServerFn({
  method: "POST",
}).handler(async (): Promise<PublishedChallengeListRow[]> => {
  const { getDBWithInit } = await import("~/lib/db");
  const { challenges, challengeFrameworks, coachingArticles } =
    await import("~/../drizzle/schema");
  const { and, count, eq, isNull } = await import("drizzle-orm");

  const db = await getDBWithInit();

  const [baseChallenges, frameworkRows, articleRows] = await Promise.all([
    db
      .select({
        id: challenges.id,
        title: challenges.title,
        slug: challenges.slug,
        subtitle: challenges.subtitle,
        description: challenges.description,
        tagline: challenges.tagline,
        icon: challenges.icon,
      })
      .from(challenges)
      .where(eq(challenges.status, ContentStatus.Published))
      .orderBy(challenges.title),
    db
      .select({
        challengeId: challengeFrameworks.challengeId,
        total: count(challengeFrameworks.frameworkId),
      })
      .from(challengeFrameworks)
      .groupBy(challengeFrameworks.challengeId),
    db
      .select({
        challengeId: coachingArticles.challengeId,
        total: count(coachingArticles.id),
      })
      .from(coachingArticles)
      .where(
        and(
          eq(coachingArticles.status, ContentStatus.Published),
          isNull(coachingArticles.draftOf),
        ),
      )
      .groupBy(coachingArticles.challengeId),
  ]);

  const frameworkCount = new Map(
    frameworkRows.map((r) => [r.challengeId, r.total] as const),
  );
  const articleCount = new Map(
    articleRows.map((r) => [r.challengeId, r.total] as const),
  );

  return baseChallenges.map((c) => ({
    ...c,
    frameworks: frameworkCount.get(c.id) ?? 0,
    articles: articleCount.get(c.id) ?? 0,
  }));
});

export const publishedChallengesQueryOptions = queryOptions({
  queryKey: challengeQueryKeys.publishedList,
  queryFn: () => getPublishedChallengesFn(),
});

export const publishedChallengeWithRelationsQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: [...challengeQueryKeys.published(slug), "relations"] as const,
    queryFn: () => getPublishedChallengeWithRelationsFn({ data: { slug } }),
  });

// ============================================================================
// Cross-entity pulls (derived from existing joins — no schema changes)
// ============================================================================

export interface ChallengeCtpRow {
  id: string;
  slug: string | null;
  name: string;
  logoUrl: string | null;
  description: string | null;
  deliveryFormat: string | null;
  accreditationLevel: string | null;
  duration: string | null;
  tuitionRange: string | null;
}

/**
 * Get CTPs that teach any framework linked to the given challenge.
 * Path: challengeFrameworks → ctpFrameworks → ctps (published, deduped).
 */
export const getCtpsByChallengeFn = createServerFn({ method: "GET" })
  .validator(z.object({ challengeId: z.string().uuid() }))
  .handler(async ({ data }): Promise<ChallengeCtpRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { challengeFrameworks, ctpFrameworks, ctps } =
      await import("~/../drizzle/schema");
    const { and, eq, isNull } = await import("drizzle-orm");
    const { resolveStoredImageUrl } = await import("~/lib/server/s3");

    const db = await getDBWithInit();

    const rows = await db
      .selectDistinctOn([ctps.id], {
        id: ctps.id,
        slug: ctps.slug,
        name: ctps.name,
        logoUrl: ctps.logoUrl,
        description: ctps.description,
        deliveryFormat: ctps.deliveryFormat,
        accreditationLevel: ctps.accreditationLevel,
        duration: ctps.duration,
        tuitionRange: ctps.tuitionRange,
      })
      .from(challengeFrameworks)
      .innerJoin(
        ctpFrameworks,
        eq(ctpFrameworks.frameworkId, challengeFrameworks.frameworkId),
      )
      .innerJoin(ctps, eq(ctps.id, ctpFrameworks.ctpId))
      .where(
        and(
          eq(challengeFrameworks.challengeId, data.challengeId),
          eq(ctps.status, ContentStatus.Published),
          isNull(ctps.draftOf),
        ),
      );

    return rows.map((r) => ({
      ...r,
      logoUrl: resolveStoredImageUrl(r.logoUrl),
    }));
  });

export interface ChallengeCcoRow {
  id: string;
  slug: string | null;
  name: string;
  logoUrl: string | null;
}

/**
 * Get CCOs that accredit the frameworks or CTPs linked to the given challenge.
 * Used as a "credentialed by" trust strip on the challenge detail page.
 * Path A: challengeFrameworks → frameworks.ccoId
 * Path B: challengeFrameworks → ctpFrameworks → ctps.ccoId
 * Union + dedupe, published only.
 */
export const getCcosByChallengeFn = createServerFn({ method: "GET" })
  .validator(z.object({ challengeId: z.string().uuid() }))
  .handler(async ({ data }): Promise<ChallengeCcoRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const {
      challengeFrameworks,
      frameworks,
      ctpFrameworks,
      ctpAccreditations,
      ctps,
      ccos,
    } = await import("~/../drizzle/schema");
    const { and, eq, isNotNull, isNull } = await import("drizzle-orm");
    const { resolveStoredImageUrl } = await import("~/lib/server/s3");

    const db = await getDBWithInit();

    const viaFrameworks = await db
      .selectDistinctOn([ccos.id], {
        id: ccos.id,
        slug: ccos.slug,
        name: ccos.name,
        logoUrl: ccos.logoUrl,
      })
      .from(challengeFrameworks)
      .innerJoin(frameworks, eq(frameworks.id, challengeFrameworks.frameworkId))
      .innerJoin(ccos, eq(ccos.id, frameworks.ccoId))
      .where(
        and(
          eq(challengeFrameworks.challengeId, data.challengeId),
          isNotNull(frameworks.ccoId),
          eq(ccos.status, ContentStatus.Published),
        ),
      );

    const viaCtps = await db
      .selectDistinctOn([ccos.id], {
        id: ccos.id,
        slug: ccos.slug,
        name: ccos.name,
        logoUrl: ccos.logoUrl,
      })
      .from(challengeFrameworks)
      .innerJoin(
        ctpFrameworks,
        eq(ctpFrameworks.frameworkId, challengeFrameworks.frameworkId),
      )
      .innerJoin(ctps, eq(ctps.id, ctpFrameworks.ctpId))
      .innerJoin(ccos, eq(ccos.id, ctps.ccoId))
      .where(
        and(
          eq(challengeFrameworks.challengeId, data.challengeId),
          eq(ccos.status, ContentStatus.Published),
          // A public strip must not be reachable from an unpublished program
          // or a draft of one, which the other public CTP queries already
          // guard against.
          eq(ctps.status, ContentStatus.Published),
          isNull(ctps.draftOf),
        ),
      );

    // `ctps.ccoId` is only the school that DELIVERS a program. The bodies that
    // accredit it stand behind the same approaches and belong under
    // "Credentialed by" too — without this the strip credits the provider and
    // omits ICF, EMCC and the rest entirely.
    const viaCtpAccreditors = await db
      .selectDistinctOn([ccos.id], {
        id: ccos.id,
        slug: ccos.slug,
        name: ccos.name,
        logoUrl: ccos.logoUrl,
      })
      .from(challengeFrameworks)
      .innerJoin(
        ctpFrameworks,
        eq(ctpFrameworks.frameworkId, challengeFrameworks.frameworkId),
      )
      .innerJoin(ctps, eq(ctps.id, ctpFrameworks.ctpId))
      .innerJoin(ctpAccreditations, eq(ctpAccreditations.ctpId, ctps.id))
      .innerJoin(ccos, eq(ccos.id, ctpAccreditations.ccoId))
      .where(
        and(
          eq(challengeFrameworks.challengeId, data.challengeId),
          eq(ccos.status, ContentStatus.Published),
          // Same guard: an accreditor attached to a draft must not appear
          // publicly before that program is published.
          eq(ctps.status, ContentStatus.Published),
          isNull(ctps.draftOf),
        ),
      );

    const seen = new Set<string>();
    const out: ChallengeCcoRow[] = [];
    for (const row of [...viaFrameworks, ...viaCtps, ...viaCtpAccreditors]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push({ ...row, logoUrl: resolveStoredImageUrl(row.logoUrl) });
    }
    return out;
  });
